import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Trash2, Link2, Plus, Globe } from "lucide-react";
import { toast } from "sonner";
import { knowledgeService, type KnowledgeItem } from "@/services/knowledgeService";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";

export default function KnowledgeDocs() {
  const { resolveProjectId } = useProjectSelection();
  const [urls, setUrls] = useState<KnowledgeItem[]>([]);
  const [pdfs, setPdfs] = useState<KnowledgeItem[]>([]);
  const [newUrl, setNewUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const projectId = resolveProjectId("TEAM");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadKnowledge = useCallback(async () => {
    try {
      setIsLoading(true);
      const items = await knowledgeService.getAll(projectId ?? "TEAM");
      
      setUrls(items.filter(item => item.type === 'url'));
      setPdfs(items.filter(item => item.type === 'pdf'));
    } catch (e) {
      toast.error("Failed to load knowledge sources");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadKnowledge();
  }, [loadKnowledge]);

  const handleAddUrl = async () => {
    if (newUrl.trim()) {
      try {
        await knowledgeService.ingestUrl(newUrl.trim(), projectId ?? "TEAM");
        await loadKnowledge();
        setNewUrl("");
        toast.success("URL added successfully");
      } catch (e) {
        toast.error("Failed to add URL");
      }
    }
  };

  const handleDeleteUrl = async (id: string) => {
    try {
      await knowledgeService.delete(id);
      await loadKnowledge();
      toast.success("URL removed successfully");
    } catch (e) {
      toast.error("Failed to remove URL");
    }
  };

  const handleDeletePdf = async (id: string) => {
    try {
      await knowledgeService.delete(id);
      await loadKnowledge();
      toast.success("Document removed successfully");
    } catch (e) {
      toast.error("Failed to remove document");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files) return;

    let successCount = 0;
    for (const file of Array.from(files)) {
      if (file.type === 'application/pdf') {
        try {
          await knowledgeService.ingestPdf(file, projectId ?? "TEAM");
          successCount++;
        } catch (e) {
          toast.error(`Failed to upload ${file.name}`);
        }
      } else {
        toast.error(`Skipped ${file.name}: Only PDF files are allowed`);
      }
    }

    if (successCount > 0) {
      await loadKnowledge();
      toast.success(`${successCount} file(s) uploaded successfully`);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    await processFiles(e.dataTransfer.files);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await processFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground mt-1">Upload documents and add reference URLs for Sophie to learn from</p>
        </div>
      </div>

      <Tabs defaultValue="pdfs" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap mb-6">
          <TabsTrigger value="pdfs">PDF Documents</TabsTrigger>
          <TabsTrigger value="urls">Reference URLs</TabsTrigger>
        </TabsList>

        <TabsContent value="pdfs" className="space-y-6">
          {/* Upload Card */}
          <Card className="p-6 card-hover">
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                isDragging 
                  ? "border-primary bg-primary/5" 
                  : "border-border hover:border-primary/50 hover:bg-accent/50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Drop PDF files here</h3>
                  <p className="text-sm text-muted-foreground">
                    or click to browse your files
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Maximum file size: 20MB
                  </p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelect}
                />
                <Button className="mt-2" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Files
                </Button>
              </div>
            </div>
          </Card>

          {/* Uploaded PDFs List */}
          <Card className="p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Uploaded Documents</h3>
              <Badge variant="secondary">{pdfs.length} files</Badge>
            </div>
            <div className="space-y-3">
              {pdfs.map((pdf) => (
                <div
                  key={pdf.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-stat-red/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-stat-red" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={pdf.title}>{pdf.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {pdf.size} • Uploaded {pdf.uploadDate}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeletePdf(pdf.id)}
                    className="hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {pdfs.length === 0 && !isLoading && (
                 <div className="text-center py-6 text-muted-foreground">
                   No PDF documents uploaded yet.
                 </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="urls" className="space-y-6">
          {/* Add URL Card */}
          <Card className="p-6 card-hover">
            <h3 className="text-lg font-semibold mb-4">Add Reference URL</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add URLs to documentation, FAQs, or other web resources for Sophie to reference
            </p>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="url"
                  placeholder="https://example.com/docs"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddUrl()}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleAddUrl}>
                <Plus className="w-4 h-4 mr-2" />
                Add URL
              </Button>
            </div>
          </Card>

          {/* URLs List */}
          <Card className="p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Reference URLs</h3>
              <Badge variant="secondary">{urls.length} URLs</Badge>
            </div>
            <div className="space-y-3">
              {urls.map((urlItem) => (
                <div
                  key={urlItem.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-chart-blue/20 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-chart-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={urlItem.url}>{urlItem.url}</p>
                      <p className="text-sm text-muted-foreground">{urlItem.title}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => urlItem.url && window.open(urlItem.url, "_blank")}
                    >
                      Visit
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteUrl(urlItem.id)}
                    className="hover:text-destructive ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              {urls.length === 0 && !isLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No reference URLs added yet</p>
                  <p className="text-sm mt-1">Add your first URL above to get started</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
